import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import Link, { type LinkProps } from 'next/link';
import * as React from 'react';

import { cn } from '@/shared/utils';

export const buttonVariants = cva(
	'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
	{
		variants: {
			variant: {
				default:
					'bg-primary text-primary-foreground shadow hover:bg-primary/90',
				destructive:
					'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
				outline:
					'border border-input bg-white shadow-sm hover:bg-accent hover:text-accent-foreground',
				secondary:
					'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
				ghost: 'hover:bg-accent hover:text-accent-foreground',
				link: 'text-primary underline-offset-4 hover:underline',
			},
			size: {
				default: 'h-9 px-4 py-2',
				sm: 'h-8 rounded-md px-3 text-xs',
				lg: 'h-10 rounded-md px-8',
				icon: 'h-9 w-9',
				'48': 'h-12 rounded-md px-4 text-base [&_svg]:size-5',
				'72': 'h-[72px] rounded-md px-8 text-base [&_svg]:size-5',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
);

const SHADCN_VARIANTS = new Set([
	'default',
	'destructive',
	'secondary',
	'ghost',
	'link',
]);

const SHADCN_SIZE_KEYS = new Set(['default', 'sm', 'lg', 'icon', '48', '72']);

const isShadcnUi = (
	variant: ButtonVisualVariant | undefined,
	size: ButtonSize | undefined,
) => {
	const v = variant ?? 'primary';
	if (SHADCN_VARIANTS.has(v)) return true;
	if (v === 'outline' && (size === 'default' || size === 'icon')) return true;
	return false;
};

const resolveShadcnSize = (
	size: ButtonSize | undefined,
): NonNullable<VariantProps<typeof buttonVariants>['size']> => {
	if (size && SHADCN_SIZE_KEYS.has(size)) {
		return size as NonNullable<VariantProps<typeof buttonVariants>['size']>;
	}
	return 'default';
};

type ButtonVisualVariant =
	| 'primary'
	| 'gray'
	| 'green'
	| 'red'
	| 'white'
	| 'outline'
	| 'plain'
	| 'plain-gray'
	| 'plain-red'
	| 'sparkles'
	| NonNullable<VariantProps<typeof buttonVariants>['variant']>;

export type ButtonSize =
	| 'xs'
	| 'sm'
	| 'md'
	| 'lg'
	| '28'
	| '36'
	| '40'
	| '44'
	| '48'
	| '72'
	| NonNullable<VariantProps<typeof buttonVariants>['size']>;

type SharedButtonProps = {
	children?: React.ReactNode;
	variant?: ButtonVisualVariant;
	size?: ButtonSize;
	loading?: boolean;
	block?: boolean;
	className?: string;
	asChild?: boolean;
	/** Кнопка или Link — один обработчик на оба DOM-типа */
	onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
};

type ButtonOnlyProps = Omit<
	React.ButtonHTMLAttributes<HTMLButtonElement>,
	'size' | keyof SharedButtonProps
>;

type LinkOnlyExtraProps = Omit<LinkProps, 'size' | keyof SharedButtonProps>;

export type ButtonProps = SharedButtonProps &
	(ButtonOnlyProps | (LinkOnlyExtraProps & { href: LinkProps['href'] })) & {
		size?: ButtonSize;
	};

export type ShadcnButtonSize = NonNullable<
	VariantProps<typeof buttonVariants>['size']
>;

const getProductButtonClassName = ({
	variant = 'primary',
	size = 'md',
	loading,
	block,
	disabled,
	className,
}: SharedButtonProps & {
	disabled?: boolean;
}) =>
	clsx(
		{
			'w-full': block,
			'h-9 px-4 text-xs': size === 'xs',
			'h-9 px-4 text-sm': size === 'sm',
			'h-12 px-4 text-base': size === 'md' || size === '48',
			'h-[52px] px-6 text-base': size === 'lg',
			'h-7 px-2 text-sm': size === '28',
			'h-9 px-5 py-2 text-sm': size === '36',
			'h-10 px-4 py-3 text-sm': size === '40',
			'h-11 px-4 py-2 text-sm': size === '44',
			'h-[72px] px-8 text-base': size === '72',

			'cursor-not-allowed opacity-90': loading || disabled,
			'bg-accent text-white': variant === 'primary',
			'bg-accent-active': variant === 'primary' && (loading || disabled),
			'hover:bg-accent-hover focus-visible:outline-accent active:bg-accent-active':
				variant === 'primary' && !(loading || disabled),
			'bg-red text-white': variant === 'red',
			'bg-red-active': variant === 'red' && (loading || disabled),
			'hover:bg-red-hover focus-visible:outline-red active:bg-red-active':
				variant === 'red' && !(loading || disabled),
			'bg-gray-5 text-gray-3': variant === 'gray',
			'bg-gray-4 text-gray-1': variant === 'gray' && (loading || disabled),
			'hover:bg-gray-4 active:bg-gray-4':
				variant === 'gray' && !(loading || disabled),
			'bg-green text-white hover:bg-green-400 focus-visible:outline-green-400 active:bg-green-400':
				variant === 'green' && !loading,
			'hover:bg-green-500 focus-visible:outline-green-400 active:bg-green-600':
				variant === 'green' && !(loading || disabled),
			'bg-white text-black ring-1 ring-gray-4': variant === 'outline',
			'hover:bg-gray-4 active:border-gray-5 active:bg-gray-5':
				variant === 'outline' && !loading,
			'bg-gray-4 text-gray-3':
				(variant === 'green' || variant === 'outline' || variant === 'white') &&
				(loading || disabled),
			'h-auto !px-0 text-accent': variant === 'plain',
			'hover:text-hover': variant === 'plain' && !(loading || disabled),
			'h-auto !px-0 text-gray-3': variant === 'plain-gray',
			'hover:text-gray-2': variant === 'plain-gray' && !(loading || disabled),
			'h-auto !px-0 text-red': variant === 'plain-red',
			'hover:text-red-hover focus-visible:outline-accent':
				variant === 'plain-red' && !(loading || disabled),
			'bg-gradient-to-r from-accent to-[#01F2FF] font-medium text-white opacity-90 transition-all hover:opacity-100':
				variant === 'sparkles',
			'bg-white text-gray-1 shadow-sm hover:bg-gray-4 active:bg-gray-5':
				variant === 'white' && !(loading || disabled),
		},
		'flex cursor-pointer items-center justify-center gap-1',
		'font-medium',
		'rounded-lg no-underline transition-all',
		'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
		className,
	);

const Button = React.forwardRef<
	HTMLButtonElement | HTMLAnchorElement,
	ButtonProps
>(
	(
		{
			variant = 'primary',
			size = 'md',
			loading,
			block,
			className,
			asChild = false,
			...props
		},
		ref,
	) => {
		const disabled = loading || ('disabled' in props ? props.disabled : false);
		const shadcn = isShadcnUi(variant, size);

		const composedClassName = shadcn
			? cn(
					buttonVariants({
						variant: variant as NonNullable<
							VariantProps<typeof buttonVariants>['variant']
						>,
						size: resolveShadcnSize(size),
					}),
					block && 'w-full',
					loading && 'cursor-wait',
					className,
				)
			: getProductButtonClassName({
					variant,
					size,
					loading,
					block,
					disabled: 'disabled' in props ? props.disabled : false,
					className,
				});

		if ('href' in props && props.href) {
			return (
				<Link
					ref={ref as React.Ref<HTMLAnchorElement>}
					className={composedClassName}
					aria-busy={loading || undefined}
					{...(props as LinkProps)}
				/>
			);
		}

		const buttonProps = props as React.ButtonHTMLAttributes<HTMLButtonElement>;

		if (asChild) {
			return (
				<Slot
					ref={ref as React.Ref<HTMLButtonElement>}
					className={composedClassName}
					aria-busy={loading || undefined}
					aria-disabled={disabled || undefined}
					{...buttonProps}
				/>
			);
		}

		return (
			<button
				ref={ref as React.Ref<HTMLButtonElement>}
				className={composedClassName}
				aria-busy={loading || undefined}
				{...buttonProps}
				type={buttonProps.type ?? 'button'}
				disabled={disabled}
			/>
		);
	},
);

Button.displayName = 'Button';

export { Button };
